import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { db } from "../firebase/firestore";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useUser } from "../context/userContext";
import { useAuthContext } from "../context/AuthContext";
import { usePremiumCheck } from './hooks/usePremiumCheck';
import SubscriptionPopup from "../Components/SubscriptionPopup";
import { BookOpen, Lock, ExternalLink } from 'lucide-react';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [userCourses, setUserCourses] = useState([]);
  const [userCoursesAccess, setUserCoursesAccess] = useState([]);
  const [giftTimers, setGiftTimers] = useState({});
  const [buttonData, setButtonData] = useState(null);
  const navigationInProgressRef = useRef(false);
  
  const { 
    premium: userIsPremium, 
    id: userId, 
    loading: userLoading,
    subscriptionData,
    userType
  } = useUser();
  
  const { user: firebaseUser } = useAuthContext();
  
  const { 
    loading: premiumLoading
  } = usePremiumCheck(userId);

  const effectivePremium = userIsPremium;

  // Получение имени коллекции в зависимости от типа пользователя
  const getCollectionName = useCallback(() => {
    if (userType === 'web') {
      return 'webUsers';
    }
    return 'telegramUsers';
  }, [userType]);

  // Функция для загрузки курсов пользователя из БД
  const fetchUserCourses = useCallback(async () => {
    if (!userId) return [];

    try {
      const collectionName = getCollectionName();
      const userDocRef = doc(db, collectionName, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const courses = userData.courses || [];
        const coursesAccess = userData.coursesAccess || [];
        setUserCourses(courses);
        setUserCoursesAccess(coursesAccess);
        return courses;
      }
      return [];
    } catch (error) {
      console.error("Error fetching user courses:", error);
      return [];
    }
  }, [userId, getCollectionName]);

  // Проверка, не истек ли доступ к курсу
  const isCourseAccessExpired = useCallback((courseId) => {
    const access = userCoursesAccess.find(a => a.id === courseId);
    if (!access || !access.endDate) return false;
    
    const now = new Date();
    const endDate = new Date(access.endDate);
    return endDate < now;
  }, [userCoursesAccess]);

  // Функция для загрузки подарков пользователя
  const fetchUserGifts = useCallback(async () => {
    if (!userId) return [];

    try {
      const collectionName = getCollectionName();
      const giftsRef = collection(db, collectionName, userId, "gifts");
      const q = query(giftsRef, where("status", "==", "active"));
      const querySnapshot = await getDocs(q);
      
      const gifts = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const expiresAt = data.expiresAt?.toDate?.() || new Date(data.expiresAt);
        const now = new Date();
        
        if (expiresAt > now) {
          gifts.push({
            id: doc.id,
            ...data,
            expiresAt: expiresAt
          });
        }
      });
      
      // Обновляем таймеры
      const newTimers = {};
      gifts.forEach(gift => {
        const expiresAt = new Date(gift.expiresAt);
        const now = new Date();
        
        if (expiresAt > now) {
          newTimers[gift.itemId] = {
            isActive: true,
            expiresAt: expiresAt,
            duration: gift.duration,
            timeLeft: Math.floor((expiresAt - now) / 1000),
            giftData: gift
          };
        }
      });
      
      setGiftTimers(newTimers);
      return gifts;
    } catch (error) {
      console.error("Error fetching user gifts:", error);
      return [];
    }
  }, [userId, getCollectionName]);

  // Загружаем данные пользователя при монтировании
  useEffect(() => {
    if (userId) {
      Promise.all([
        fetchUserCourses(),
        fetchUserGifts()
      ]);
    }
  }, [userId, fetchUserCourses, fetchUserGifts]);

  // Обновляем таймеры подарков каждую секунду
  useEffect(() => {
    if (!userId || Object.keys(giftTimers).length === 0) return;

    const interval = setInterval(() => {
      setGiftTimers(prev => {
        const updated = { ...prev };
        let hasChanges = false;

        Object.keys(updated).forEach(key => {
          if (updated[key].timeLeft > 0) {
            updated[key].timeLeft -= 1;
            hasChanges = true;
          } else {
            delete updated[key];
            hasChanges = true;
          }
        });

        return hasChanges ? updated : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [userId, giftTimers]);

  // Функция для обработки нажатия кнопки назад
  const handleBackButton = useCallback(() => {
    if (navigationInProgressRef.current) {
      console.log('Navigation already in progress, skipping');
      return;
    }

    console.log('CourseDetail: Back button clicked, navigating back');
    navigationInProgressRef.current = true;
    
    try {
      navigate(-1);
    } catch (error) {
      console.error('Navigation error:', error);
      navigate('/', { replace: true });
    } finally {
      setTimeout(() => {
        navigationInProgressRef.current = false;
      }, 300);
    }
  }, [navigate]);

  // Telegram WebApp инициализация (только для Telegram пользователей)
  useEffect(() => {
    if (userType === 'telegram' && window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      
      tg.ready();
      tg.expand();
      
      tg.BackButton.backgroundColor = '#000000';
      tg.BackButton.textColor = '#ffffff';
      
      tg.BackButton.offClick();
      tg.BackButton.onClick(handleBackButton);
      tg.BackButton.isVisible = true;
      
      console.log('CourseDetail: Telegram back button initialized');
    }

    return () => {
      if (userType === 'telegram' && window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        tg.BackButton.offClick(handleBackButton);
        tg.BackButton.isVisible = false;
      }
    };
  }, [handleBackButton, userType]);

  // Сбрасываем флаг навигации при изменении маршрута
  useEffect(() => {
    navigationInProgressRef.current = false;
  }, [location.pathname]);

  // Функция для поиска кнопки в блоках курса
  const findButtonInCourse = (courseData) => {
    if (!courseData?.blocks) return null;
    
    for (const block of courseData.blocks) {
      if (!block.contents) continue;
      
      const buttonContent = block.contents.find(content => content.type === "button");
      if (buttonContent) {
        console.log("Найдена кнопка в курсе:", buttonContent);
        return buttonContent.data;
      }
    }
    
    return null;
  };

  useEffect(() => {
    const loadCourse = async () => {
      try {
        const courseRef = doc(db, "courses", id);
        const courseSnap = await getDoc(courseRef);
        
        if (courseSnap.exists()) {
          const courseData = {
            id: courseSnap.id,
            ...courseSnap.data()
          };
          setCourse(courseData);
          
          // Ищем кнопку в курсе
          const button = findButtonInCourse(courseData);
          setButtonData(button);
          
          // Фильтруем блоки, убирая блоки с кнопками
          if (courseData.blocks) {
            const filteredBlocks = courseData.blocks.map(block => {
              if (block.contents) {
                block.contents = block.contents.filter(content => content.type !== "button");
              }
              return block;
            }).filter(block => block.contents?.length > 0);
            courseData.blocks = filteredBlocks;
          }
        } else {
          console.error("Course not found");
        }
      } catch (error) {
        console.error("Error loading course:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadCourse();
    }
  }, [id]);

  // Функция для проверки доступа к курсу
  const checkCourseAccess = useCallback(() => {
    if (!course) return { hasAccess: false, reason: 'no_course' };
    
    console.log(`🔍 Проверка доступа к курсу ${course.title || course.id}:`, {
      courseId: course.id,
      userId,
      userType,
      isPremium: effectivePremium,
      userCourses,
      giftTimers: giftTimers[course.id]
    });

    // 1. Проверка подарка
    if (userId && giftTimers[course.id]) {
      return {
        hasAccess: true,
        reason: 'gift',
        message: 'Подарок',
        giftData: giftTimers[course.id]
      };
    }

    // 2. Проверка индивидуального доступа через courses с проверкой срока
    if (userId && userCourses.includes(course.id)) {
      const isExpired = isCourseAccessExpired(course.id);
      if (!isExpired) {
        console.log(`✅ Доступ разрешен: курс ${course.id} найден в массиве courses пользователя`);
        return {
          hasAccess: true,
          reason: 'user_course',
          message: 'Индивидуальный доступ'
        };
      } else {
        console.log(`❌ Доступ к курсу истек: ${course.id}`);
      }
    }
    
    // 3. Проверка бесплатного курса
    if (!course.premium && !course.paidAccessId) {
      return {
        hasAccess: true,
        reason: 'free',
        message: 'Доступен'
      };
    }

    // 4. Проверка авторизации
    if (!userId) {
      return {
        hasAccess: false,
        reason: 'no_auth',
        message: 'Требуется авторизация',
        requiresAuth: true
      };
    }

    // 5. Проверка Premium подписки
    if (course.premium && effectivePremium) {
      return {
        hasAccess: true,
        reason: 'premium',
        message: 'Premium доступ'
      };
    }

    // 6. Проверка paidAccessId
    if (course.paidAccessId) {
      if (course.paidAccessId === userId) {
        console.log(`✅ Доступ разрешен: paidAccessId ${course.paidAccessId} совпадает с userId`);
        return {
          hasAccess: true,
          reason: 'paid_access',
          message: 'Персональный доступ'
        };
      }
    }

    // 7. Для веб-пользователей проверка по email
    if (userType === 'web' && firebaseUser?.email && course.paidAccessEmail) {
      if (course.paidAccessEmail === firebaseUser.email) {
        console.log(`✅ Доступ разрешен: paidAccessEmail ${course.paidAccessEmail} совпадает с email пользователя`);
        return {
          hasAccess: true,
          reason: 'paid_access_email',
          message: 'Персональный доступ'
        };
      }
    }

    // 8. Требуется Premium
    if (course.premium) {
      return {
        hasAccess: false,
        reason: 'premium_required',
        message: 'Premium',
        requiresPremium: true
      };
    }

    return {
      hasAccess: false,
      reason: 'unknown',
      message: 'Premium'
    };
  }, [course, userId, userType, effectivePremium, userCourses, giftTimers, isCourseAccessExpired, firebaseUser]);

  const access = checkCourseAccess();
  const showPremiumBadge = course?.premium || course?.paidAccessId;

  let accessBadge = null;
  if (access.hasAccess) {
    if (access.reason === 'gift') {
      accessBadge = { text: '🎁 Подарок', color: 'from-pink-500 to-purple-500' };
    } else if (access.reason === 'user_course') {
      accessBadge = { text: '👤 Индивидуальный', color: 'from-blue-500 to-cyan-500' };
    } else if (access.reason === 'premium') {
      accessBadge = { text: '⭐ PRO', color: 'from-yellow-500 to-orange-500' };
    } else if (access.reason === 'paid_access' || access.reason === 'paid_access_email') {
      accessBadge = { text: '🔑 Персональный', color: 'from-green-500 to-emerald-500' };
    } else if (access.reason === 'free') {
      accessBadge = { text: '🆓 Бесплатный', color: 'from-gray-500 to-gray-600' };
    }
  }

  const handleBlockClick = (block) => {
    if (access.hasAccess) {
      navigate(`/course/${course.id}/block/${block.id}`, { 
        state: { 
          blockId: block.id,
          block: block,
          courseId: course.id,
          course: course
        }
      });
    } else if (access.requiresPremium) {
      setShowSubscriptionPopup(true);
    } else if (access.requiresAuth) {
      alert("Пожалуйста, авторизуйтесь");
    } else {
      alert("Этот курс временно недоступен");
    }
  };

  const handleButtonClick = () => {
    if (buttonData?.url) {
      window.open(buttonData.url, buttonData.target || '_blank');
    }
  };

  const handleGetPremium = () => {
    setShowSubscriptionPopup(true);
  };

  const formatTimeLeft = (seconds) => {
    if (!seconds || seconds <= 0) return "00:00";
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (days > 0) {
      return `${days}д ${hours}ч`;
    } else if (hours > 0) {
      return `${hours}ч ${minutes}м`;
    } else if (minutes > 0) {
      return `${minutes}м ${secs}с`;
    } else {
      return `${secs}с`;
    }
  };

  if (loading || userLoading || premiumLoading) {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <div className="text-white text-lg">Загрузка курса...</div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="text-white text-xl">Курс не найден</div>
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: -1,
        }}
      />

      <div className="w-full min-h-screen px-4 pt-6 pb-24 relative z-10">
        {/* Заголовок курса */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-white text-[24px] font-semibold">
              {course.title}
            </h1>
            {showPremiumBadge && !accessBadge && (
              <span className="inline-block px-2 py-1 bg-gradient-to-r from-[#FF4757] to-[#FF6B6B] rounded-lg text-white text-xs font-medium shadow-lg">
                Premium
              </span>
            )}
            {accessBadge && (
              <span className={`inline-block px-2 py-1 rounded-lg text-white text-xs font-medium shadow-lg bg-gradient-to-r ${accessBadge.color}`}>
                {accessBadge.text}
              </span>
            )}
          </div>
          
          {course.description && (
            <p className="text-gray-400 text-sm mt-2">{course.description}</p>
          )}

          {access.hasAccess && access.reason === 'gift' && access.giftData && (
            <div className="mt-3 inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg">
              <span className="mr-1">🎁</span>
              Подарок активен: {formatTimeLeft(access.giftData.timeLeft)}
            </div>
          )}
        </div>

        {/* Статус доступа */}
        {userId && !access.hasAccess && access.requiresPremium && !effectivePremium && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-500/40">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium flex items-center">
                  🔒 PRO не активирован
                </p>
                <p className="text-sm text-white/70 mt-1">
                  Этот курс требует PRO подписки
                </p>
              </div>
              <button 
                className="px-6 py-2 rounded-full text-white text-[15px] font-medium active:scale-95 transition bg-gradient-to-r from-[#6C6FF8] to-[#F466C8] hover:opacity-90"
                onClick={handleGetPremium}
              >
                Получить PRO
              </button>
            </div>
          </div>
        )}

        {/* Список блоков */}
        <div className="flex flex-col gap-4">
          {course.blocks && course.blocks.length > 0 ? (
            course.blocks.map((block, index) => {
              const isLocked = !access.hasAccess;
              
              return (
                <div
                  key={block.id}
                  className={`w-full rounded-[20px] overflow-hidden transition-all relative ${
                    isLocked ? 'opacity-90 border border-red-500/30' : ''
                  }`}
                  style={{
                    background: "linear-gradient(135deg, rgba(36,19,82,0.95) 0%, rgba(26,11,53,0.98) 100%)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <div className="absolute top-3 right-3 z-10">
                    <span 
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isLocked 
                          ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
                          : 'bg-green-500/20 text-green-300 border border-green-500/30'
                      }`}
                    >
                      {isLocked ? "Заблокировано" : "Доступно"}
                    </span>
                  </div>

                  <div className="p-4 flex items-start gap-4">
                    <div className="relative">
                      <div className="w-[88px] h-[88px] rounded-[16px] overflow-hidden shrink-0 bg-black/30 flex items-center justify-center">
                        {block.image ? (
                          <img 
                            src={block.image} 
                            alt={block.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              const parent = e.target.parentElement;
                              if (parent) {
                                parent.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-10 h-10 text-purple-400" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></div>';
                              }
                            }}
                          />
                        ) : (
                          <BookOpen className="w-10 h-10 text-purple-400" />
                        )}
                      </div>
                    </div>

                    <div className="flex-1 flex flex-col">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 pr-16">
                          <h3 className={`text-[18px] leading-tight font-medium ${
                            access.hasAccess ? 'text-[#C8AFE8]' : 'text-gray-400'
                          }`}>
                            {block.title}
                          </h3>
                          
                          {block.description && (
                            <p className="text-sm text-gray-400 mt-1 line-clamp-2">
                              {block.description}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="mt-3">
                        <button
                          className={`px-4 py-1.5 rounded-full text-white text-[13px] font-medium active:scale-95 transition ${isLocked ? 'cursor-not-allowed' : 'hover:opacity-90'}`}
                          style={{
                            background: isLocked ? "#6B7280" : "#6B3FD8",
                          }}
                          onClick={() => !isLocked && handleBlockClick(block)}
                          disabled={isLocked}
                        >
                          {isLocked ? "Заблокировано" : "Смотреть"}
                        </button>
                        
                        {isLocked && access.requiresPremium && !effectivePremium && userId && (
                          <p className="text-xs text-yellow-400 mt-2 text-center">
                            Требуется Premium подписка
                          </p>
                        )}
                        
                        {isLocked && !userId && (
                          <p className="text-xs text-pink-400 mt-2 text-center">
                            Требуется авторизация
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 bg-white/5 rounded-2xl backdrop-blur-sm">
              <p className="text-white/60">В этом курсе пока нет блоков</p>
            </div>
          )}
        </div>

        {/* Кнопка из курса */}
        {buttonData && (
          <div className="w-full flex justify-center mt-8">
            <div
              onClick={handleButtonClick}
              className="
                w-full max-w-[343px]
                h-[52px]
                flex items-center justify-center
                gap-3
                rounded-full
                px-6
                text-white
                font-medium
                text-[16px]
                cursor-pointer
                select-none
                bg-gradient-to-r from-[#6A5AE0] to-[#8E6BFF]
                shadow-[0_6px_20px_rgba(142,107,255,0.35)]
                hover:shadow-[0_8px_28px_rgba(142,107,255,0.55)]
                active:scale-[0.97]
                transition-all duration-200
              "
            >
              <div className="flex items-center justify-center w-6 h-6 shrink-0">
                <ExternalLink className="w-5 h-5 text-white" />
              </div>

              <div className="flex items-center justify-center">
                <span className="leading-none tracking-[0.2px]">
                  {buttonData.text || "Перейти"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <SubscriptionPopup
        isOpen={showSubscriptionPopup}
        onClose={() => setShowSubscriptionPopup(false)}
        subscriptionInfo={{
          active: effectivePremium,
          userId: userId,
          stripeCustomerId: subscriptionData?.stripeCustomerId,
          price: subscriptionData?.price || 55,
          currency: subscriptionData?.currency || '€',
          period: 'в месяц',
          features: [
            'Полный доступ ко всем курсам',
            'Доступ к премиум материалам',
            'Персональная обратная связь',
            'Доступ к закрытому сообществу'
          ]
        }}
      />
    </>
  );
};

export default CourseDetail;